import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { IconExclamation } from '@components/icons/exclamation';
import { IconTextFormatLinkOff } from '@components/icons/text-format-link-off';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

type JourneyFormLoadErrorProps = {
  isTemporary: boolean;
  onRetry: () => void;
};

export const JourneyFormLoadError = ({ isTemporary, onRetry }: JourneyFormLoadErrorProps) => {
  const { t } = useScopedTranslate(locale);

  if (isTemporary) {
    return (
      <EmptyState
        header={t('temporaryHeader')}
        content={
          <Stack space={4}>
            <Text color="textSecondary" font="regular14" text={t('temporaryDesc')} />
          </Stack>
        }
        icon={<IconExclamation size={80} />}
        action={<Button text={t('tryAgain')} variant="primary" onClick={onRetry} />}
      />
    );
  }

  return (
    <EmptyState
      header={t('header')}
      content={
        <Stack space={4}>
          <Text color="textSecondary" font="regular14" text={t('desc1')} />
          <Text color="textSecondary" font="regular14" text={t('desc2')} />
        </Stack>
      }
      icon={<IconTextFormatLinkOff size={80} />}
    />
  );
};

const locale = {
  en: {
    header: 'Link inactive',
    desc1: 'The form link you tried to reach is not accessible at the moment.',
    desc2: 'The owner may have disabled or removed it. Please contact them if you need access.',
    temporaryHeader: 'Something went wrong',
    temporaryDesc: 'We could not load this form just now. Please try again in a moment.',
    tryAgain: 'Try again',
  },
  is: {
    header: 'Hlekkurinn er óvirkur',
    desc1: 'Ekki er hægt að opna hlekkinn á þetta form eins og er.',
    desc2:
      'Eigandinn kann að hafa gert það óvirkt eða eytt því. Hafðu samband við hann ef þú þarft aðgang.',
    temporaryHeader: 'Eitthvað fór úrskeiðis',
    temporaryDesc: 'Ekki tókst að hlaða forminu í þetta skiptið. Reyndu aftur eftir augnablik.',
    tryAgain: 'Reyna aftur',
  },
  dk: {
    header: 'Linket er inaktivt',
    desc1: 'Det formularlink, du forsøgte at åbne, er ikke tilgængeligt i øjeblikket.',
    desc2:
      'Ejeren kan have deaktiveret eller fjernet det. Kontakt dem, hvis du har brug for adgang.',
    temporaryHeader: 'Noget gik galt',
    temporaryDesc: 'Vi kunne ikke indlæse formularen lige nu. Prøv igen om et øjeblik.',
    tryAgain: 'Prøv igen',
  },
};
